import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { VisitorData } from '@/lib/analytics/googleAnalyticsData';

interface VisitorsChartProps {
  data: VisitorData[];
  isLoading?: boolean;
}

/**
 * Graphique d'évolution des visiteurs
 */
export function VisitorsChart({ data, isLoading }: VisitorsChartProps) {
  // Transformer les données pour recharts
  const chartData = data.map((item) => ({
    date: item.date,
    visitors: item.visitors,
    formattedDate: format(parseISO(item.date), 'dd MMM', { locale: fr }),
  }));

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Évolution des visiteurs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-sm text-gray-500">Chargement...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Évolution des visiteurs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-sm text-gray-500">Aucune donnée disponible</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Évolution des visiteurs</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="formattedDate"
              stroke="#888888"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="#888888"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${value}`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '8px 12px',
              }}
              labelStyle={{ fontWeight: 600, marginBottom: '4px' }}
              formatter={(value: number) => [`${value} visiteurs`, 'Visiteurs']}
            />
            <Line
              type="monotone"
              dataKey="visitors"
              stroke="#55BC8C"
              strokeWidth={2}
              dot={{ fill: '#55BC8C', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
